import { useState } from "react";
import { useSelector } from "react-redux";
import { Target, AlertCircle, Percent, Tag } from "lucide-react";
import { SelectUserWebsiteURL } from '../../../../DesignEditor/designEditorSlice';

export default function PricingTestConfig({
    testType,
    formData,
    onUpdate,
    onNext,
    initialProduct = null,
    initialPrices = null,
    currency = 'USD',
    storeUrl = '',
    cf_username = ''
}) {
    const websiteUrl = useSelector(SelectUserWebsiteURL);
    
    // Initialize from formData if available
    const [productName, setProductName] = useState(
        formData?.variants?.selectedProduct?.name || 
        initialProduct?.name || 
        ''
    );
    const [pageUrl, setPageUrl] = useState(
        formData?.variants?.selectedProduct?.pageUrl || 
        initialProduct?.pageUrl || 
        ''
    );
    const [priceSelector, setPriceSelector] = useState(
        formData?.variants?.selectedProduct?.priceSelector || 
        initialProduct?.priceSelector || 
        ''
    );
    const [discountPercentage, setDiscountPercentage] = useState(
        formData?.variants?.selectedProduct?.discountPercentage?.toString() || 
        initialProduct?.discountPercentage?.toString() || 
        ''
    );
    const [couponCode, setCouponCode] = useState(
        formData?.variants?.selectedProduct?.couponCode || 
        initialProduct?.couponCode || 
        ''
    );
    const [errors, setErrors] = useState({});
    const [testId] = useState(() => `test_${Date.now()}`);

    const validateSelector = (selector) => {
        if (!selector.trim()) {
            return 'CSS selector is required';
        }
        return null;
    };

    const validatePageUrl = (url) => {
        if (!url.trim()) {
            return 'Page URL is required';
        }
        try {
            new URL(url);
            return null;
        } catch (error) {
            return 'Invalid URL format';
        }
    };

    const validatePercentage = (value) => {
        const num = parseFloat(value);
        if (isNaN(num) || num <= 0 || num > 100) {
            return 'Percentage must be between 1 and 100';
        }
        return null;
    };

    const handlePageUrlChange = (value) => {
        setPageUrl(value);
        if (value.trim()) {
            const error = validatePageUrl(value);
            setErrors(prev => {
                if (error) {
                    return { ...prev, pageUrl: error };
                } else {
                    const newErrors = { ...prev };
                    delete newErrors.pageUrl;
                    return newErrors;
                }
            });
        } else {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.pageUrl;
                return newErrors;
            });
        }
    };

    const handleSelectorChange = (value) => {
        setPriceSelector(value);
        setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.priceSelector;
            return newErrors;
        });
    };

    const handlePercentageChange = (value) => {
        setDiscountPercentage(value);
        if (value.trim()) {
            const error = validatePercentage(value);
            setErrors(prev => {
                if (error) {
                    return { ...prev, discountPercentage: error };
                } else {
                    const newErrors = { ...prev };
                    delete newErrors.discountPercentage;
                    return newErrors;
                }
            });
        } else {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.discountPercentage;
                return newErrors;
            });
        }
    };

    const hasValidInput = () => {
        const nameValid = productName.trim().length > 0;
        const urlValid = pageUrl.trim().length > 0 && !errors.pageUrl;
        const selectorValid = priceSelector.trim().length > 0 && !errors.priceSelector;
        const percentageValid = discountPercentage.trim().length > 0 && !errors.discountPercentage;
        const couponValid = couponCode.trim().length > 0;
        
        return nameValid && urlValid && selectorValid && percentageValid && couponValid && Object.keys(errors).length === 0;
    };

    const handleContinue = () => {
        const newErrors = {};

        if (!productName.trim()) {
            newErrors.name = 'Product name is required';
        }

        const urlError = validatePageUrl(pageUrl);
        if (urlError) {
            newErrors.pageUrl = urlError;
        }

        const selectorError = validateSelector(priceSelector);
        if (selectorError) {
            newErrors.priceSelector = selectorError;
        }

        const percentageError = validatePercentage(discountPercentage);
        if (percentageError) {
            newErrors.discountPercentage = percentageError;
        }

        if (!couponCode.trim()) {
            newErrors.couponCode = 'Coupon code is required';
        }

        setErrors(newErrors);

        if (Object.keys(newErrors).length === 0) {
            const productConfig = {
                name: productName.trim(),
                pageUrl: pageUrl.trim(),
                priceSelector: priceSelector.trim(),
                discountPercentage: parseFloat(discountPercentage),
                couponCode: couponCode.trim(),
                type: 'custom',
                detectionMethod: 'manual'
            };

            // IMPORTANT: Update parent with the correct structure
            onUpdate({
                variants: {
                    selectedProduct: productConfig,
                    testPrice: null
                }
            });

            // Move to Step 3 (Settings)
            onNext();
        }
    };

    const openSelectorTool = () => {
        const targetUrl = pageUrl.trim() || websiteUrl || storeUrl;

        if (!targetUrl) {
            alert('Please enter a Product Page URL first');
            return;
        }

        const previewParams = new URLSearchParams({
            cf_element_selection: 'true',
            test_id: testId,
            mode: 'price_selector',
            return_url: window.location.href
        });

        const url = new URL(targetUrl);
        previewParams.forEach((value, key) => {
            url.searchParams.set(key, value);
        });

        const previewUrl = url.toString();
        window.open(previewUrl, '_blank', 'noopener,noreferrer');

        startListeningForSelection();
    };

    const startListeningForSelection = () => {
        const handleSelection = (event) => {
            if (event.data?.type === 'CF_ELEMENT_SELECTED' && 
                event.data?.mode === 'price_selector') {
                const { selector } = event.data;
                setPriceSelector(selector);
                setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors.priceSelector;
                    return newErrors;
                });
                window.removeEventListener('message', handleSelection);
            }
        };

        window.addEventListener('message', handleSelection);

        setTimeout(() => {
            window.removeEventListener('message', handleSelection);
        }, 600000);
    };

    return (
        <div className="p-0">
            {/* Main Form Card */}
            <div className="bg-white rounded-xl border-2 border-gray-200 shadow-sm overflow-hidden max-w-4xl mx-auto">
                {/* Product Details Section */}
                <div className="p-5 border-b border-gray-200">
                    <h4 className="text-base font-semibold text-slate-800 mb-4 flex items-center gap-2">
                        <span className="flex items-center justify-center w-6 h-6 bg-blue-100 text-blue-600 rounded-full text-xs font-bold">1</span>
                        Product Details
                    </h4>

                    <div className="space-y-4">
                        {/* Product Page URL */}
                        <div>
                            <label className="block text-sm font-medium text-gray-900 mb-1.5">
                                Product Page URL <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="url"
                                value={pageUrl}
                                onChange={(e) => handlePageUrlChange(e.target.value)}
                                placeholder="https://yourstore.com/products/example"
                                className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                                    errors.pageUrl ? 'border-red-500' : 'border-gray-300'
                                }`}
                            />
                            {errors.pageUrl && (
                                <div className="flex items-center gap-1.5 mt-1 text-xs text-red-500">
                                    <AlertCircle size={12} />
                                    <span>{errors.pageUrl}</span>
                                </div>
                            )}
                        </div>

                        {/* Price Element Selector */}
                        <div>
                            <label className="block text-sm font-medium text-gray-900 mb-1.5">
                                Price Element Selector <span className="text-red-500">*</span>
                            </label>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={priceSelector}
                                    onChange={(e) => handleSelectorChange(e.target.value)}
                                    placeholder=".product-price, #price, [data-price]"
                                    className={`flex-1 px-3 py-2 border rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                                        errors.priceSelector ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                />
                                <button
                                    type="button"
                                    onClick={openSelectorTool}
                                    className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition-all whitespace-nowrap"
                                >
                                    <Target size={16} />
                                    Find Price
                                </button>
                            </div>
                            {errors.priceSelector && (
                                <div className="flex items-center gap-1.5 mt-1 text-xs text-red-500">
                                    <AlertCircle size={12} />
                                    <span>{errors.priceSelector}</span>
                                </div>
                            )}
                            <p className="text-xs text-gray-600 mt-1">
                                CSS selector for the price element you want to modify
                            </p>
                        </div>

                        {/* Product Name */}
                        <div>
                            <label className="block text-sm font-medium text-gray-900 mb-1.5">
                                Product Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={productName}
                                onChange={(e) => {
                                    setProductName(e.target.value);
                                    if (errors.name && e.target.value.trim()) {
                                        setErrors(prev => {
                                            const newErrors = { ...prev };
                                            delete newErrors.name;
                                            return newErrors;
                                        });
                                    }
                                }}
                                placeholder="Enter product name for identification"
                                className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                                    errors.name ? 'border-red-500' : 'border-gray-300'
                                }`}
                            />
                            {errors.name && (
                                <div className="flex items-center gap-1.5 mt-1 text-xs text-red-500">
                                    <AlertCircle size={12} />
                                    <span>{errors.name}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Discount Configuration Section */}
                <div className="p-5 border-b border-gray-200 bg-gray-50">
                    <h4 className="text-base font-semibold text-slate-800 mb-4 flex items-center gap-2">
                        <span className="flex items-center justify-center w-6 h-6 bg-purple-100 text-purple-600 rounded-full text-xs font-bold">2</span>
                        Discount Configuration
                    </h4>

                    <div className="space-y-4">
                        {/* Discount Percentage */}
                        <div>
                            <label className="block text-sm font-medium text-gray-900 mb-1.5">
                                Discount Percentage <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <input
                                    type="number"
                                    min="1"
                                    max="100"
                                    step="1"
                                    value={discountPercentage}
                                    onChange={(e) => handlePercentageChange(e.target.value)}
                                    placeholder="10"
                                    className={`w-full px-3 py-2 pr-10 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                                        errors.discountPercentage ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                />
                                <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 text-gray-500 pointer-events-none">
                                    <Percent size={16} />
                                </div>
                            </div>
                            {errors.discountPercentage && (
                                <div className="flex items-center gap-1.5 mt-1 text-xs text-red-500">
                                    <AlertCircle size={12} />
                                    <span>{errors.discountPercentage}</span>
                                </div>
                            )}
                            <p className="text-xs text-gray-600 mt-1">
                                Group B will see the price reduced by this percentage
                            </p>
                        </div>

                        {/* Coupon Code */}
                        <div>
                            <label className="block text-sm font-medium text-gray-900 mb-1.5">
                                Coupon Code <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                                    <Tag size={16} />
                                </div>
                                <input
                                    type="text"
                                    value={couponCode}
                                    onChange={(e) => {
                                        setCouponCode(e.target.value.toUpperCase());
                                        if (errors.couponCode && e.target.value.trim()) {
                                            setErrors(prev => {
                                                const newErrors = { ...prev };
                                                delete newErrors.couponCode;
                                                return newErrors;
                                            });
                                        }
                                    }}
                                    placeholder="SAVE10"
                                    className={`w-full pl-10 pr-3 py-2 border rounded-lg text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                                        errors.couponCode ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                />
                            </div>
                            {errors.couponCode && (
                                <div className="flex items-center gap-1.5 mt-1 text-xs text-red-500">
                                    <AlertCircle size={12} />
                                    <span>{errors.couponCode}</span>
                                </div>
                            )}
                            <p className="text-xs text-gray-600 mt-1">
                                Coupon code that will be tracked for conversions in this test
                            </p>
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div className="p-5 bg-gray-50">
                    {/* Validation Messages */}
                    {Object.keys(errors).length > 0 && (
                        <div className="mb-4 flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <AlertCircle size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-red-900">
                                Please fix the errors above before continuing.
                            </span>
                        </div>
                    )}

                    <div className="flex justify-end gap-3">
                        <button
                            onClick={() => {
                                setProductName('');
                                setPageUrl('');
                                setPriceSelector('');
                                setDiscountPercentage('');
                                setCouponCode('');
                                setErrors({});
                            }}
                            className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all"
                        >
                            Reset Form
                        </button>
                        <button
                            onClick={handleContinue}
                            disabled={!hasValidInput()}
                            className={`px-6 py-2 text-sm font-semibold rounded-lg transition-all ${
                                hasValidInput()
                                    ? 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-lg hover:-translate-y-0.5'
                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            }`}
                        >
                            Continue to Test Settings
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}